import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Mail, Clock, Calendar } from 'lucide-react';
import ScanResultsView from '../components/ScanResultsView';
import { decodeMimeWords } from '../utils/mime';

function ScanResult() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scanResult, setScanResult] = useState(null);
  const [scanMeta, setScanMeta] = useState(null);

  useEffect(() => {
    const fetchScan = async () => {
      try {
        const res = await axios.get(`/scan/history/${id}`);
        const scan = res.data.scan;
        if (!scan) { setError('Scan not found'); return; }

        const result = {
          overall: {
            score: scan.score ?? scan.overall_score ?? 0,
            action: scan.action ?? scan.overall_action ?? 'unknown',
            is_spam: scan.is_spam ?? false,
            subject: scan.subject ?? ''
          },
          results: {},
          filename: scan.subject
        };

        // New format: rspamd_results/spamassassin_results/ai_results from Go backend
        if (scan.rspamd_results) {
          const r = typeof scan.rspamd_results === 'string' ? JSON.parse(scan.rspamd_results) : scan.rspamd_results;
          result.results.rspamd = r;
        }
        if (scan.spamassassin_results) {
          const s = typeof scan.spamassassin_results === 'string' ? JSON.parse(scan.spamassassin_results) : scan.spamassassin_results;
          result.results.spamassassin = s;
        }
        if (scan.ai_results) {
          const a = typeof scan.ai_results === 'string' ? JSON.parse(scan.ai_results) : scan.ai_results;
          result.results.ai = a;
        }

        // Legacy format fallback: symbols/messages from old Node.js backend
        if (!result.results.rspamd && scan.symbols) {
          const symbols = typeof scan.symbols === 'string' ? JSON.parse(scan.symbols) : scan.symbols;
          if (symbols?.rspamd) {
            result.results.rspamd = { symbols: symbols.rspamd, rules_matched: Object.keys(symbols.rspamd).length, action: scan.action, score: scan.score };
          }
          if (symbols?.spamassassin) {
            result.results.spamassassin = { symbols: Object.keys(symbols.spamassassin), symbol_details: symbols.spamassassin, rules_matched: Object.keys(symbols.spamassassin).length, is_spam: scan.is_spam };
          }
          if (symbols?.ai) {
            result.results.ai = symbols.ai;
          }
        }

        setScanMeta({
          subject: scan.subject || scan.email_subject || '',
          scanTime: scan.scan_time || scan.scan_time_total || 0,
          scanDate: scan.scan_date || scan.created_at || '',
          scanMethod: scan.scan_method || 'manual',
        });
        setScanResult(result);
      } catch (err) {
        setError(err.response?.data?.error || err.response?.data?.message || 'Failed to load scan');
      } finally {
        setLoading(false);
      }
    };
    fetchScan();
  }, [id]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px] gap-3">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
        <span className="text-gray-500">Loading scan result...</span>
      </div>
    );
  }

  if (error) {
    return <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 rounded">{error}</div>;
  }

  if (!scanResult) return null;

  const formatDate = (d) => d ? new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
  const formatScanTime = (ms) => {
    if (!ms) return '';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  return (
    <>
      <Helmet><title>{scanMeta?.subject ? `${decodeMimeWords(scanMeta.subject)} - ` : ''}Scan Result - Rspamd Email Scanner</title></Helmet>
      <button onClick={() => navigate(-1)} className="text-blue-600 hover:text-blue-800 mb-4 inline-block">&larr; Back</button>

      {scanMeta && (
        <div className="bg-white rounded-xl shadow-sm border p-5 mb-5">
          {scanMeta.subject && (
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-5 h-5 text-gray-400 flex-shrink-0" />
              <h2 className="text-lg font-semibold truncate">{decodeMimeWords(scanMeta.subject)}</h2>
            </div>
          )}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
            {scanMeta.scanDate && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(scanMeta.scanDate)}
              </span>
            )}
            {scanMeta.scanTime > 0 && (
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {formatScanTime(scanMeta.scanTime)}
              </span>
            )}
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              {scanMeta.scanMethod === 'email' ? 'Email scan' : 'Manual scan'}
            </span>
          </div>
        </div>
      )}

      <ScanResultsView scanResult={scanResult} />
    </>
  );
}

export default ScanResult;
